import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import CustomerLayout from './components/CustomerLayout';
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import AdminLogin from './pages/AdminLogin';
import SocietyAdminLogin from './pages/SocietyAdminLogin';
import SocietyAdminLayout from './components/SocietyAdminLayout';
import SocietyReconciliationGate from './components/SocietyReconciliationGate';
import SocietyAdminReconciliation from './pages/SocietyAdminReconciliation';
import SocietyAdminLedger from './pages/SocietyAdminLedger';
import SocietyAdminReconcile from './pages/SocietyAdminReconcile';
import SocietyAdminOverview from './pages/SocietyAdminOverview';
import SocietyAdminBillPayments from './pages/SocietyAdminBillPayments';
import SocietyAdminDetails from './pages/SocietyAdminDetails';
import SocietyAdminFlats from './pages/SocietyAdminFlats';
import SocietyAdminFlatHistory from './pages/SocietyAdminFlatHistory';
import SocietyAdminGuards from './pages/SocietyAdminGuards';
import SocietyAdminCommittee from './pages/SocietyAdminCommittee';
import SocietyAdminPendingRequests from './pages/SocietyAdminPendingRequests';
import SocietyAdminComplaints from './pages/SocietyAdminComplaints';
import SocietyAdminCreateBill from './pages/SocietyAdminCreateBill';
import SocietyAdminRecordPayment from './pages/SocietyAdminRecordPayment';
import SocietyAdminScheduledBills from './pages/SocietyAdminScheduledBills';
import AdminDashboard from './pages/AdminDashboard';
import AdminAppVersions from './pages/AdminAppVersions';
import AdminPromotions from './pages/AdminPromotions';
import AdminParticipatingEventsList from './pages/AdminParticipatingEventsList';
import AdminParticipatingEventForm from './pages/AdminParticipatingEventForm';
import AdminParticipatingEventInterests from './pages/AdminParticipatingEventInterests';
import AdminStatistics from './pages/AdminStatistics';
import AdminSocietyOverview from './pages/AdminSocietyOverview';
import AdminSocieties from './pages/AdminSocieties';
import AdminSocietyDetail from './pages/AdminSocietyDetail';
import AdminSocietySettings from './pages/AdminSocietySettings';
import AdminSocietyFlatDetail from './pages/AdminSocietyFlatDetail';
import AdminSocietyFlatMembers from './pages/AdminSocietyFlatMembers';
import AdminCustomers from './pages/AdminCustomers';
import AdminCustomerDetail from './pages/AdminCustomerDetail';
import AdminCategoryModules from './pages/AdminCategoryModules';
import AdminParserBuilder from './pages/AdminParserBuilder';
import AdminSocietyOnboard from './pages/AdminSocietyOnboard';
import CustomerLogin from './pages/CustomerLogin';
import CustomerRegister from './pages/CustomerRegister';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerPromotions from './pages/CustomerPromotions';
import CustomerStatistics from './pages/CustomerStatistics';
import ConditionalWhatsApp from './components/ConditionalWhatsApp';
import ResidentHomeLoader from './components/resident/ResidentHomeLoader';
import './App.css';

const ResidentRoutes = React.lazy(() => import('./routes/ResidentRoutes'));

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route
            path="/resident-home/*"
            element={
              <Suspense fallback={<ResidentHomeLoader caption="Loading…" />}>
                <ResidentRoutes />
              </Suspense>
            }
          />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/promotions"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminPromotions />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/category-modules"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminCategoryModules />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participating-events/new"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParticipatingEventForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participating-events/:id/edit"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParticipatingEventForm />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participating-events/:id/interests"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParticipatingEventInterests />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participating-events"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParticipatingEventsList />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/statistics"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminStatistics />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/society-overview"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietyOverview />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/societies/:societyId/flats/:flatId"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietyFlatDetail />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/societies/:id/flat-members"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietyFlatMembers />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/societies/:id/settings"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietySettings />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/societies/:id"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietyDetail />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/societies"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocieties />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/onboard-society"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminSocietyOnboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/parser-builder/:id"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParserBuilder />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/parser-builder"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminParserBuilder />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminCustomers />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customers/:id"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminCustomerDetail />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/force-update"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminAppVersions />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Customer routes */}
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route
            path="/customer/dashboard"
            element={
              <ProtectedRoute role="customer">
                <CustomerLayout>
                  <CustomerDashboard />
                </CustomerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/promotions"
            element={
              <ProtectedRoute role="customer">
                <CustomerLayout>
                  <CustomerPromotions />
                </CustomerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/statistics"
            element={
              <ProtectedRoute role="customer">
                <CustomerLayout>
                  <CustomerStatistics />
                </CustomerLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Society Admin routes */}
          <Route path="/society/login" element={<SocietyAdminLogin />} />
          <Route
            path="/society/reconciliation"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyReconciliationGate>
                    <SocietyAdminReconcile />
                  </SocietyReconciliationGate>
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/overview"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminOverview />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/ledger"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminLedger />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/reconcile"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminReconcile />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/interest-config"
            element={
              <ProtectedRoute role="society_admin">
                <Navigate to="/society/details#interest-config" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/bill-categories"
            element={
              <ProtectedRoute role="society_admin">
                <Navigate to="/society/details#bill-categories" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/bill-payments"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyReconciliationGate>
                    <SocietyAdminBillPayments />
                  </SocietyReconciliationGate>
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/details"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminDetails />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/flats"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminFlats />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/flat-history"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminFlatHistory />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/guards"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminGuards />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/pending-requests"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminPendingRequests />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/committee"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminCommittee />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/complaints"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminComplaints />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/create-bill"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminCreateBill />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/record-payment"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminRecordPayment />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/society/scheduled-bills"
            element={
              <ProtectedRoute role="society_admin">
                <SocietyAdminLayout>
                  <SocietyAdminScheduledBills />
                </SocietyAdminLayout>
              </ProtectedRoute>
            }
          />

          {/* Redirect /admin to /admin/dashboard, /customer to /customer/dashboard */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/customer" element={<Navigate to="/customer/dashboard" replace />} />
          <Route path="/society" element={<Navigate to="/society/overview" replace />} />
        </Routes>
        <ConditionalWhatsApp />
      </Router>
    </AuthProvider>
  );
}

export default App;
